import { buildSearchText } from "@nomnom/core";
import type { StudentProfile, User } from "@nomnom/core";
import { collection, doc, onSnapshot, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";

type StudentProfileMap = Record<string, StudentProfile>;
export type FilterValue = "all" | "active" | "inactive" | "verified" | "unverified";

export type UserRow = {
  uid: string;
  name: string;
  email: string;
  phone: string;
  college: string;
  dorm: string;
  totalOrders: number;
  totalSpent: number;
  rating: number;
  verified: boolean;
  active: boolean;
  joinedAt?: User["createdAt"];
};

export function subscribeToStudentUsers(
  onUpdate: (users: User[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "users"),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map(
          (docSnap) => ({ uid: docSnap.id, ...docSnap.data() }) as User,
        ),
      );
    },
    (error) => {
      console.error("[UsersService] Failed to load users:", error);
      onError("Failed to load users.");
    },
  );
}

export function subscribeToStudentProfiles(
  onUpdate: (profiles: StudentProfileMap) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "studentProfiles"),
    (snapshot) => {
      const profileMap: StudentProfileMap = {};
      snapshot.docs.forEach((docSnap) => {
        profileMap[docSnap.id] = {
          uid: docSnap.id,
          ...docSnap.data(),
        } as StudentProfile;
      });

      onUpdate(profileMap);
    },
    (error) => {
      console.error("[UsersService] Failed to load student profiles:", error);
      onError("Failed to load student profiles.");
    },
  );
}

export function buildUserRows(
  users: User[],
  studentProfiles: StudentProfileMap,
): UserRow[] {
  return users
    .filter((user) => Boolean(user.roles?.student))
    .map((user) => {
      const profile = studentProfiles[user.uid];

      return {
        uid: user.uid,
        name: user.displayName || user.email || "Unknown user",
        email: user.email || "No email",
        phone: user.phoneNumber || "No phone",
        college: profile?.college || "Unknown",
        dorm: profile?.dormName || profile?.dormId || "Unknown",
        totalOrders: profile?.totalOrders || 0,
        totalSpent: profile?.totalSpent || 0,
        rating: profile?.ratingAvg || 0,
        verified: Boolean(profile?.ucsdEmail),
        active: user.active ?? true,
        joinedAt: user.createdAt,
      };
    });
}

export function filterUserRows(
  users: UserRow[],
  filter: FilterValue,
  search: string,
): UserRow[] {
  const normalizedSearch = search.trim().toLowerCase();

  return users.filter((user) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "active" && user.active) ||
      (filter === "inactive" && !user.active) ||
      (filter === "verified" && user.verified) ||
      (filter === "unverified" && !user.verified);

    if (!matchesFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return buildSearchText([user.name, user.email, user.college, user.dorm]).includes(
      normalizedSearch,
    );
  });
}

export async function updateStudentUserStatus(
  uid: string,
  desiredActive: boolean,
  adminNotes: string,
): Promise<void> {
  try {
    await updateDoc(doc(db, "users", uid), {
      active: desiredActive,
      adminNotes: adminNotes.trim() || null,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("[UsersService] Failed to update user:", error);
    throw new Error("Failed to update user status.");
  }
}
